import useWidgetViewManager, { unavailableErrorOf } from '@/composables/widget-view-manager/useWidgetViewManager'
import { useWorkflow } from '@/composables/workflow/useWorkflow'
import { promiseTimeout, until, useEventBus } from '@vueuse/core'
import { WebSocketConnectionError } from '@/composables/session/websocket/useWebsocket'
import { updateSentryContext } from '@/utils/sentry'
import { setupTracker } from '@/composables/tracker/useTracker'
import { updateOrchestratorApi } from '@/composables/orchestrator/useOrchestratorApi'
import { updateAppsApi } from '@/composables/apps-api/useAppsApi'
import { useI18n } from '../i18n/useI18n'
import useEmbed from '@/embed/code'
import { computed, ref } from 'vue'
import type { ValidateTokenResponseError } from '@/composables/orchestrator/types'
import type { TokenDecoded } from '@/composables/workflow/useWorkflow'
import type { WidgetOptions } from '@/composables/widget-options/useWidgetOptions'
import useWidgetOptions from '@/composables/widget-options/useWidgetOptions'
import useClientDomain from '@/composables/client-domain/useClientDomain'
import { useCustomCss } from '../custom-css/useCustomCss'
import { useRemoteWidgetOptions } from '../remote-widget-options/use-remote-widget-options'
import type { AxiosError } from 'axios'
import { setForcedBreakpoint } from 'widget-components/composables/breakpoints'
import { logger } from '@sentry/vue'
import { path, pick } from 'ramda'
import useWidgetSession, { useSessionContext } from '@/composables/session/useWidgetSession'
import { resolveStrategy } from '@/composables/session/strategy-registry'
import { SESSION_EXPIRED, UnsupportedTokenVersionError, WORKFLOW_CANCELLED } from '@/composables/session/types'

const INITIALIZATION_NO_CONTEXT = 'initialization-no-context'
const ALREADY_PREINITIALIZED = 'already preinitialized'

const preInitialized = ref(false)
const preInitializing = ref(false)
const preInitializationFailed = ref(false)
const initialized = ref(false)
const widgetOpened = ref(false)
const initializing = ref(false)
let startRun = 0

function useWidgetHooks() {
    const preInitialize = async (token: string, options?: Partial<WidgetOptions>, clientDomain?: string) => {
        logger.trace(`Preinitialize called`, {
            token: !!token,
            options: options,
        })

        useClientDomain().setClientDomain(clientDomain)

        const workspace = useWorkflow()
        const { setOptions } = useWidgetOptions()
        const { setupCustomCss } = useCustomCss()
        const { initialize: initializeWidgetViewManager } = useWidgetViewManager()
        const { t } = useI18n()

        if (preInitializing.value) {
            return
        }

        try {
            if (preInitialized.value) {
                throw new Error(ALREADY_PREINITIALIZED)
            }
            if (!token) {
                throw INITIALIZATION_NO_CONTEXT
            }

            preInitializing.value = true
            preInitializationFailed.value = false

            let decodedToken = {} as TokenDecoded

            try {
                decodedToken = workspace.setToken(token)
            } catch (error) {
                useWidgetViewManager().showError({
                    title: t('views.widget.invalid_token.title'),
                    description: t('views.widget.invalid_token.description'),
                    error,
                    retry: false,
                    close: true,
                    svgIcon: 'warning.svg',
                })

                useEmbed().emitErrorEvent({
                    code: 'fail',
                    message: 'token decode error',
                })

                preInitializationFailed.value = true

                return
            }

            logger.trace(`T0ken decoded`)

            // exp is a NumericDate (seconds), Date.now() is milliseconds
            if (decodedToken.exp && decodedToken.exp * 1000 <= Date.now()) {
                throw new Error(SESSION_EXPIRED)
            }

            useWidgetSession().setStrategy(resolveStrategy(decodedToken.version, decodedToken.engineType))

            if (options) {
                setOptions(options)
            }

            const mode = workspace.mode.value

            const { partyId, workflowDefinitionId, workspaceId, version, workflowId } = decodedToken

            updateSentryContext({
                partyId,
                workspaceId,
                workflowDefinitionId,
                version,
                mode,
                workflowId,
            })

            logger.info('Sentry Context updated', {
                partyId,
                workspaceId,
                workflowDefinitionId,
                version,
                mode,
                workflowId,
            })

            setupTracker(partyId, workflowDefinitionId, mode, workspaceId, clientDomain)

            updateOrchestratorApi(mode)
            updateAppsApi(mode)

            logger.trace(`Api clients setup`)

            await useWidgetSession().strategy!.preConnect(useSessionContext())

            const mergedOptions: Partial<WidgetOptions> = {
                ...options || {},
            }

            try {
                const remoteOptions = await useRemoteWidgetOptions().getOptions()

                if (remoteOptions) {
                    const { widgetOptions, cssVariables } = remoteOptions

                    const usedRemoteOptions = options?.tlHosted || import.meta.env.DEV
                        ? widgetOptions?.hosted
                        : widgetOptions?.embedded

                    Object.assign(mergedOptions, {
                        ...usedRemoteOptions,
                        cssVariables,
                    })
                }

                logger.trace(remoteOptions
                    ? `Widget options downloaded and merged`
                    : `Received empty widget options from API`, {
                    widgetOptions: mergedOptions,
                })
            } catch (e: unknown) {
                logger.trace('Get remote widget options error', {
                    error: e,
                })
                console.error(e) // eslint-disable-line no-console
            }

            setOptions(mergedOptions)
            useEmbed().setWrapperOptions(mergedOptions as WidgetOptions)

            if (mergedOptions.autoHeight) {
                useEventBus('app-auto-height-request').emit()
            }

            try {
                await useWidgetSession().strategy!.validateSession(useSessionContext())
            } catch (e: unknown) {
                const err = e as AxiosError<ValidateTokenResponseError> | undefined
                const { message, errors } = err?.response?.data || {}

                const isTokenInvalid = !!message?.toLocaleLowerCase().match(/unauthorized|not authorized/)
                const isSessionExpired = Array.isArray(errors) &&
                                         errors.some(e => e.code === 'token.notFound')

                if (isSessionExpired) {
                    useWidgetViewManager().showError({
                        title: t('views.widget.session_expired.title'),
                        description: t('views.widget.session_expired.description'),
                        retry: false,
                        close: true,
                        svgIcon: 'warning.svg',
                    })
                    useEmbed().emitErrorEvent({
                        code: 'fail',
                        message: 'Session expired',
                    })
                }

                if (isTokenInvalid) {
                    useWidgetViewManager().showError({
                        title: t('views.widget.invalid_token.title'),
                        description: t('views.widget.invalid_token.description'),
                        retry: false,
                        close: true,
                        svgIcon: 'bug.svg',
                    })
                    useEmbed().emitErrorEvent({
                        code: 'fail',
                        message: 'invalid token',
                    })
                }

                preInitializationFailed.value = true

                return
            }

            initializeWidgetViewManager()

            await setupCustomCss()

            preInitialized.value = true
            logger.trace(`Preinitialized successfully`)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)

            if (!(e instanceof Error && e.message === ALREADY_PREINITIALIZED)) {
                useWidgetSession().strategy?.resetRun()
                preInitializationFailed.value = true
            }

            handleInitializationError(e)
        } finally {
            preInitializing.value = false
        }
    }

    const handleInitializationError = (e: unknown) => {
        const { t } = useI18n()
        const { showError } = useWidgetViewManager()
        const { emitErrorEvent } = useEmbed()

        const axiosError = e as AxiosError<{ errors?: Record<'message' | 'code', string>[] } | undefined>

        const errors = axiosError.response?.data?.errors || []

        const isSessionExpiredError = e instanceof Error &&
                                      e.message === SESSION_EXPIRED

        const isWorkflowTerminated = errors.some(e => e.code === 'workflow.terminated')

        if (e instanceof Error && e.message === WORKFLOW_CANCELLED) {
            useWidgetViewManager().showCancelledWorkflowError()
            emitErrorEvent({
                code: 'fail',
                message: 'Workflow cancelled',
            })

            return
        }

        if (isSessionExpiredError || isWorkflowTerminated) {
            useWidgetViewManager().showError({
                title: t('views.widget.session_expired.title'),
                description: t('views.widget.session_expired.description'),
                retry: false,
                close: true,
                svgIcon: 'warning.svg',
            })
            emitErrorEvent({
                code: 'fail',
                message: 'Session expired',
            })

            return
        }

        if (e === INITIALIZATION_NO_CONTEXT || axiosError.code === 'ERR_BAD_REQUEST') {
            showError({
                title: t('views.widget.initialization_failed.title'),
                description: t('views.widget.initialization_failed.description'),
                error: e,
                retry: false,
                close: true,
                svgIcon: 'bug.svg',
            })

            emitErrorEvent({
                code: 'fail',
                message: 'Initialization failed',
            })

            return
        }

        const errorMessages = [e, path(['message'], e)]

        if (errorMessages.includes(ALREADY_PREINITIALIZED)) {
            emitErrorEvent({
                code: 'fail',
                message: 'Already preinitialized',
            })
            return
        }

        if (e instanceof UnsupportedTokenVersionError) {
            showError({
                title: t('views.widget.invalid_token.title'),
                description: t('views.widget.invalid_token.description'),
                retry: false,
                close: true,
                svgIcon: 'warning.svg',
            })

            emitErrorEvent({
                code: 'fail',
                message: 'unsupported t0ken version',
            })

            return
        }

        const unavailable = unavailableErrorOf(e)

        if (unavailable) {
            showError({
                title: t('views.widget.initialization_failed.title'),
                description: t(`${unavailable.translationKey}.description`),
                error: new Error(t(`${unavailable.translationKey}.title`)),
                retry: true,
                close: true,
                svgIcon: 'bug.svg',
            })

            emitErrorEvent({
                code: 'fail',
                message: unavailable.event,
            })

            return
        }

        if (e instanceof WebSocketConnectionError) {
            showError({
                title: t('views.widget.socket_error.title'),
                description: t('views.widget.socket_error.description'),
                retry: true,
                close: true,
                svgIcon: 'bug.svg',
                error: e,
                errorDetails: e,
            })

            emitErrorEvent({
                code: 'fail',
                message: `WebSocket error ${e.code}`,
            })

            return
        }

        showError({
            title: t('views.widget.initialization_failed.title'),
            description: t('views.widget.initialization_failed.description'),
            error: e,
            retry: true,
            close: true,
            svgIcon: 'bug.svg',
        })

        emitErrorEvent({
            code: 'fail',
            message: 'Initialization failed',
        })
    }

    const initialize = async () => {
        const { options } = useWidgetOptions()
        const { t } = useI18n()

        if (options.value.customPagePreview) {
            setWidgetOpened(true)
            initialized.value = true
            return
        }

        logger.trace(`Initialize called`, {
            preInitialized: preInitialized.value,
            preInitializing: preInitializing.value,
        })

        try {
            await until(() => preInitialized.value || preInitializationFailed.value)
                .toBe(true, { timeout: 10 * 1000, throwOnTimeout: true })
        } catch { // eslint-disable-line local-rules/no-error-swallowing
            logger.error('Until preInitialized timeout')

            if (options.value.autoHeight) {
                useEventBus('app-auto-height-request').emit()
            }

            useWidgetViewManager().showError({
                title: t('views.widget.initialization_timeout.title'),
                description: t('views.widget.initialization_timeout.description'),
                close: true,
                svgIcon: 'timeout.svg',
            })

            useEmbed().emitErrorEvent({
                code: 'fail',
                message: 'preinitialization timeout',
            })
        }

        if (preInitializationFailed.value) {
            logger.trace(`Start workflow call prevented due to failed preinitialization`)
            return
        }

        if (options.value.explicitStart) {
            logger.trace(`Start workflow call prevented due to explicitStart=true`)
        } else {
            await startWorkflow()
        }
    }

    const startWorkflow = async () => {
        let run = startRun
        const isAbandoned = () => run !== startRun

        try {
            logger.trace(`Start workflow called`)

            const { changeLang } = useI18n()
            const { showFirstView } = useWidgetViewManager()
            const strategy = useWidgetSession().strategy!

            const { token, decodedToken, workflowId } = useWorkflow()

            if (!token.value) {
                throw new Error('Run transactionlink.setOptions({ token }) first')
            }
            if (initialized.value || initializing.value || widgetOpened.value) {
                logger.warn('already initialized, skipping')
                return
            }

            initializing.value = true
            run = ++startRun

            setWidgetOpened(true)

            await strategy.resolveWorkflow(useSessionContext())

            if (isAbandoned() || !token.value || !workflowId.value) {
                return
            }

            await strategy.ensureConnected(useSessionContext())

            if (isAbandoned()) {
                return
            }

            logger.trace(`Websocket initialized`)

            const workflow = await strategy.start(useSessionContext())

            if (isAbandoned()) {
                return
            }

            const lang = workflow?.locale || decodedToken.value?.lang

            if (lang) {
                changeLang(lang)
                logger.trace(`Language set: ${lang}`)
            }

            logger.trace(`Workflow started`)

            const firstView = await strategy.awaitFirstView(useSessionContext())

            if (isAbandoned()) {
                return
            }

            if (firstView) {
                logger.trace(`First message received`, {
                    message: firstView.raw
                        ? pick(
                            ['partyId', 'taskStatus', 'partyId', 'providerName', 'taskId', 'taskName', 'taskStatus', 'taskType', 'workflowDefinitionId', 'workflowId', 'workflowStatus', 'workspaceId'],
                            firstView.raw,
                        )
                        : firstView.context,
                })

                showFirstView(firstView)
            }

            initialized.value = true
            // bypass for reopening widget with defined token
            preInitialized.value = true

            logger.trace(`Initialized successfully`)
        } catch (error) {
            if (!isAbandoned()) {
                handleInitializationError(error)
            }
        } finally {
            if (!isAbandoned()) {
                initializing.value = false
            }
        }
    }

    const reinitialize = async () => {
        logger.trace(`Reinitialize called`)

        resetInitialization()

        await promiseTimeout(1000)

        await initialize()

        logger.trace(`Reinitialized successfully`)
    }

    const resetInitialization = () => {
        const { strategy, transport } = useWidgetSession()

        if (strategy) {
            strategy.resetRun()
        } else {
            useWidgetViewManager().resetView()
            transport.reset()
        }

        setForcedBreakpoint(undefined)
        initialized.value = false
        initializing.value = false
        startRun += 1

        setWidgetOpened(false)
    }

    const initializeCustomPagePreview = () => {
        const { options, setOptions } = useWidgetOptions()
        const { initialize: initializeWidgetViewManager } = useWidgetViewManager()
        const { setupCustomCssForPreview } = useCustomCss()

        if (!options.value.customPagePreview) {
            setOptions({ customPagePreview: true })
        }
        setForcedBreakpoint(options.value.forcedBreakpoint)

        initializeWidgetViewManager()
        setWidgetOpened(true)
        setTimeout(() => {
            setupCustomCssForPreview()
        }, 0)
    }

    const setWidgetOpened = (val: boolean) => {
        widgetOpened.value = val
    }

    return {
        preInitialized,
        preInitializationFailed,
        preInitialize,
        initialized,
        initialize,
        startWorkflow,
        reinitialize,
        resetInitialization,
        initializeCustomPagePreview,
        widgetOpened: computed(() => widgetOpened.value),
        setWidgetOpened,
    }

}

export default useWidgetHooks
